import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@/i18n';
import { ApiError, api } from '@/api/client';
import type { FamilyOverview, FamilyRelation } from '@/api/types';
import { Alert, Button, Card, Chip, Field, Input, Mail, Select, Skeleton, Users, WhatsApp, useToast } from '@/components/ui';
import { formatDate } from '@/lib/format';
import { copyText, mailtoHref, shareUrl, whatsappHref } from '@/features/care/share';

export const FAMILY_RELATIONS: FamilyRelation[] = ['co_parent', 'grandparent', 'relative', 'caregiver', 'other'];

/**
 * Family access: invite a co-parent or grandparent to view reports (read-only), see who has access,
 * and the families whose reports you can view.
 */
export const FamilySettings = ({ accountName }: { accountName: string }) => {
  const t = useT();
  const toast = useToast();
  const [data, setData] = useState<FamilyOverview>();
  const [failed, setFailed] = useState(false);
  const [form, setForm] = useState({ name: '', relation: 'co_parent' as FamilyRelation, channel: 'whatsapp' as 'whatsapp' | 'email', identifier: '' });
  const [link, setLink] = useState<{ url: string; name: string; channel: 'whatsapp' | 'email'; identifier: string }>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const load = () => api.family.get().then(setData, () => setFailed(true));
  useEffect(() => {
    void load();
  }, []);

  const invite = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const { token } = await api.family.invite({ ...form, name: form.name.trim(), identifier: form.identifier.trim() });
      setLink({ url: shareUrl(`/join/${token}`), name: form.name.trim(), channel: form.channel, identifier: form.identifier.trim() });
      setForm({ ...form, name: '', identifier: '' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const first = accountName.trim().split(/\s+/)[0] || t('family.someone');
  const message = link ? t('family.message', { name: link.name, parent: first, link: link.url }) : '';

  return (
    <Card id="family" className="mb-4 scroll-mt-24 space-y-5">
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-sage-100 text-sage-600">
          <Users className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-h3 text-ink">{t('family.title')}</h2>
          <p className="mt-1 text-body text-ink-soft">{t('family.body')}</p>
        </div>
      </div>
      {error && <Alert tone="warn">{error}</Alert>}

      {failed ? (
        <Alert tone="warn">{t('common.error')}</Alert>
      ) : !data ? (
        <Skeleton lines={3} />
      ) : (
        <>
          {(data.members.length > 0 || data.invites.length > 0) && (
            <ul className="divide-y divide-line rounded-lg border border-line">
              {data.members.map((m) => (
                <li key={m.memberAccountId} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                  <span>
                    <span className="block text-label text-ink">{m.name}</span>
                    <span className="block text-caption text-ink-muted">
                      {t(`family.relation.${m.relation}`)} · {t('family.since', { date: formatDate(m.addedAt) })}
                    </span>
                  </span>
                  <Button variant="tertiary" onClick={() => void api.family.removeMember(m.memberAccountId).then(load)}>
                    {t('family.remove')}
                  </Button>
                </li>
              ))}
              {data.invites.map((i) => (
                <li key={i.inviteId} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                  <span>
                    <span className="block text-label text-ink">{i.name}</span>
                    <span className="block text-caption text-ink-muted">
                      {i.identifier} · {i.state === 'expired' ? t('family.expired') : t('family.pending', { date: formatDate(i.expiresAt) })}
                    </span>
                  </span>
                  <Button variant="tertiary" onClick={() => void api.family.cancelInvite(i.inviteId).then(load)}>
                    {t('family.cancel')}
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {link ? (
            <div className="grid gap-3 rounded-lg bg-mint-50 p-4">
              <p className="text-label text-ink">{t('family.linkReady', { name: link.name })}</p>
              <p className="rounded-md bg-white px-3 py-2 text-caption break-all">{link.url}</p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => void copyText(link.url).then((ok) => toast(ok ? t('share.copied') : t('common.error'), { tone: ok ? 'progress' : 'alert' }))}>
                  {t('share.copy')}
                </Button>
                {link.channel === 'whatsapp' ? (
                  <Button as="a" href={whatsappHref(message)} target="_blank" rel="noopener noreferrer" variant="secondary" icon={<WhatsApp className="h-4 w-4" />}>
                    WhatsApp
                  </Button>
                ) : (
                  <Button as="a" href={mailtoHref(t('family.subject', { parent: first }), message, link.identifier)} variant="secondary" icon={<Mail className="h-4 w-4" />}>
                    {t('share.email')}
                  </Button>
                )}
                <Button variant="tertiary" onClick={() => setLink(undefined)}>
                  {t('family.another')}
                </Button>
              </div>
              <p className="text-caption text-ink-muted">{t('family.linkNote')}</p>
            </div>
          ) : (
            <form className="grid gap-3" onSubmit={(e) => void invite(e)} noValidate>
              <p className="text-label text-ink">{t('family.invite')}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field id="family-name" label={t('family.name')}>
                  <Input value={form.name} maxLength={80} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </Field>
                <Field id="family-relation" label={t('family.relationLabel')}>
                  <Select value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value as FamilyRelation })}>
                    {FAMILY_RELATIONS.map((r) => (
                      <option key={r} value={r}>
                        {t(`family.relation.${r}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div role="radiogroup" aria-label={t('family.channel')} className="flex flex-wrap gap-2">
                <Chip role="radio" selected={form.channel === 'whatsapp'} onToggle={() => setForm({ ...form, channel: 'whatsapp' })}>
                  {t('family.byWhatsapp')}
                </Chip>
                <Chip role="radio" selected={form.channel === 'email'} onToggle={() => setForm({ ...form, channel: 'email' })}>
                  {t('family.byEmail')}
                </Chip>
              </div>
              <Field
                id="family-identifier"
                label={form.channel === 'email' ? t('family.email') : t('family.phone')}
                hint={form.channel === 'email' ? undefined : t('privacy.nominee.phoneHint')}
              >
                <Input
                  type={form.channel === 'email' ? 'email' : 'tel'}
                  inputMode={form.channel === 'email' ? 'email' : 'tel'}
                  value={form.identifier}
                  onChange={(e) => setForm({ ...form, identifier: e.target.value })}
                />
              </Field>
              <p className="text-caption text-ink-muted">{t('family.readOnly')}</p>
              <div>
                <Button type="submit" variant="secondary" loading={busy} disabled={!form.name.trim() || form.identifier.trim().length < 6}>
                  {t('family.create')}
                </Button>
              </div>
            </form>
          )}

          {data.access.length > 0 && (
            <section aria-labelledby="can-view" className="border-t border-line pt-5">
              <h3 id="can-view" className="text-label text-ink">{t('family.canView')}</h3>
              <ul className="mt-2 grid gap-2">
                {data.access.map((a) => (
                  <li key={a.ownerAccountId} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-paper px-4 py-2.5">
                    <Link to={`/family/${a.ownerAccountId}`} className="text-label text-lagoon-700 hover:underline">
                      {t('family.reportsOf', { name: a.ownerName })} →
                    </Link>
                    <Button variant="tertiary" onClick={() => void api.family.leave(a.ownerAccountId).then(load)}>
                      {t('family.leave')}
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </Card>
  );
};
